// Create New Order validation rules.
// V1-ported requirements for new order creation: moveType, rate zone, broker
// fields for certain move types, and leg structure validation.

export interface CreateOrderValidationInput {
  moveType: string;
  rateZone: string;              // ordersall.templatetype ("Pickup Rate Zone")
  brokerName: string;
  brokerContactName: string;
  billToContactName: string;
  legs: Array<{ legtype: string; legAction2: string }>;
  /** Templates ride the same create flow (#33471) but NOT the same requirements.
   *  V1 splits them explicitly: the broker / broker-contact / bill-to-contact trio
   *  sits inside `if($isneworder) { if(!$istemplate) {` (lib_editrecord.pl:1471),
   *  so it is order-only. Rate Zone IS required on a template, but by its own
   *  always-on gate in useOrderEditing (#33468) — running it here too would report
   *  the same problem twice under two different names. */
  isTemplate?: boolean;
}

export function createOrderValidationErrors(
  input: CreateOrderValidationInput
): string[] {
  const errors: string[] = [];

  // Rule 1: moveType required
  if ((input.moveType ?? '').trim() === '') {
    errors.push('Move Type is required');
  }

  // Rule 2: rateZone required. Templates get this from the always-on gate instead
  // (it applies to every template save, not just the create).
  if (!input.isTemplate && (input.rateZone ?? '').trim() === '') {
    errors.push('Rate Zone is required');
  }

  // Rule 3: For IMPORT, EXPORT, or RAIL TO PORT, broker fields are required —
  // ORDERS only (V1 lib_editrecord.pl:1471-1484, `$isneworder && !$istemplate`).
  // A blueprint carries no broker or contact assignments.
  const moveType = (input.moveType ?? '').trim();
  if (!input.isTemplate
      && (moveType === 'IMPORT' || moveType === 'EXPORT' || moveType === 'RAIL TO PORT')) {
    if ((input.brokerName ?? '').trim() === '') {
      errors.push('Broker is required');
    }
    if ((input.brokerContactName ?? '').trim() === '') {
      errors.push('Broker Contact is required');
    }
    if ((input.billToContactName ?? '').trim() === '') {
      errors.push('Bill To Contact is required');
    }
  }

  // Rule 4: legs must contain at least one PICKUP and at least one DELIVERY
  const hasPickup = input.legs.some((leg) => leg.legtype === 'PICKUP');
  const hasDelivery = input.legs.some((leg) => leg.legAction2 === 'DELIVERY');

  const noun = input.isTemplate ? 'template' : 'order';
  if (!hasPickup) {
    errors.push(`The ${noun} must have a Pickup leg`);
  }
  if (!hasDelivery) {
    errors.push(`The ${noun} must have a Delivery leg`);
  }

  return errors;
}

// cms_lookups.py's document-type list spells it exactly this way.
const DELIVERY_ORDER_DOCTYPE = 'DELIVERY ORDER';

const hasDeliveryOrder = (types: string[]): boolean =>
  types.some((t) => (t ?? '').trim().toUpperCase() === DELIVERY_ORDER_DOCTYPE);

/** Move types that never carry a Delivery Order. A DOMESTIC move (e.g. a
 *  chassis relocation dry run) has no DO to upload — V1 never demanded one and
 *  users were forced to attach a dummy doc just to get past the gate. Kept as
 *  a set so the next exemption is a one-line change. */
const DELIVERY_ORDER_EXEMPT_MOVETYPES = new Set(['DOMESTIC']);

/**
 * A new order must arrive with a Delivery Order document. Two sources satisfy
 * it: a doc staged in this create session, or one already on the chosen
 * template (template docs are copied by reference at create time).
 *
 * Exempt: the move types in DELIVERY_ORDER_EXEMPT_MOVETYPES. Pass the
 * EFFECTIVE move type (edited-else-saved) — the user can change it after the
 * create modal, and a blank/unknown type still requires the doc.
 *
 * Create-only by design — once the pro exists, an ordinary save never runs
 * this check. Dupe is exempt for free: it commits through
 * POST /orders/{pronumber}/dupe, which never enters the drawer's create mode,
 * so neither copy-docs setting can trip this gate. Don't "fix" that.
 *
 * Enforced client-side only, and it has to be: staged docs are uploaded AFTER
 * the create POST returns (replayStagedDocs), so the backend can't see them.
 *
 * `templateDocTypes` is null when the template's doc list isn't known yet
 * (query in flight or failed). That FAILS OPEN — a false block would be
 * unclearable, since the doc the user is missing lives on the template and
 * isn't visible on the staged Docs tab.
 */
export function deliveryOrderDocError(
  stagedDocTypes: string[],
  templateDocTypes: string[] | null,
  moveType: string,
): string | null {
  if (DELIVERY_ORDER_EXEMPT_MOVETYPES.has((moveType ?? '').trim().toUpperCase())) return null;
  if (templateDocTypes === null) return null;
  if (hasDeliveryOrder(stagedDocTypes) || hasDeliveryOrder(templateDocTypes)) return null;
  return 'A Delivery Order document is required to create an order.';
}

export interface CreateOrderWarningInput {
  /** invoiceMain.quoteNumber — 0 is V1's "no quote" sentinel. */
  quoteNumber: unknown;
  lineItems: Array<{ item?: string; glCode?: string; qty?: unknown; rate?: unknown }>;
  /** Names the record in the advisory text — templates share this flow (#33471). */
  isTemplate?: boolean;
}

/**
 * Soft, create-only advisories: the user is about to create an order with no
 * quote and/or nothing to invoice. These are NOT errors — the caller shows them
 * in a confirm and the user may continue. Blank means "nothing billable at
 * all": no rows, or every row entirely empty. A half-filled row counts as a
 * line item here; the hard field-9 validation owns that case.
 */
export function createOrderWarnings(input: CreateOrderWarningInput): string[] {
  const warnings: string[] = [];
  // Templates ride the same create flow (#33471), so the warnings have to name
  // the record the user is actually looking at.
  const noun = input.isTemplate ? 'template' : 'order';

  if (!Number(input.quoteNumber ?? 0)) {
    warnings.push(`This ${noun} has no Quote #.`);
  }

  const num = (v: unknown): number => {
    const n = parseFloat(String(v ?? ''));
    return Number.isNaN(n) ? 0 : n;
  };
  const anyBillable = input.lineItems.some(
    (r) =>
      String(r.item ?? '').trim() !== '' ||
      String(r.glCode ?? '').trim() !== '' ||
      num(r.qty) !== 0 ||
      num(r.rate) !== 0,
  );
  if (!anyBillable) {
    warnings.push(`This ${noun} has no invoice line items.`);
  }

  return warnings;
}
